import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const linkedinLimitsArticle: BlogArticle = {
  key: BlogArticleKey.LINKEDIN_LIMITS,
  slug: BlogSlugs.LINKEDIN_LIMITS,
  categoryId: "04",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Prospection LinkedIn : ce qui fait restreindre un compte",
      excerpt:
        "Les limites ne sont pas publiées, et les outils qui promettent du volume les franchissent pour toi. Ce qui déclenche vraiment une restriction, et comment travailler sans la craindre.",
      description:
        "Invitations, messages, automatisation : ce qui expose réellement un compte LinkedIn à une restriction, et la façon de prospecter sans dépendre d'un outil qui agit à ta place.",
      ctaTitle: "Prospecte moins, mais mieux ciblé",
      ctaText:
        "On qualifie ton réseau avec des critères tirés de tes appels, pour que tu contactes vingt personnes qui répondent plutôt que deux cents au hasard. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La question revient dans toutes les conversations sur la prospection LinkedIn, et elle n'a jamais de réponse claire : à partir de quand risque-t-on de se faire restreindre le compte.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Elle n'a pas de réponse claire parce que LinkedIn ne publie pas de seuils. Et le vide est occupé par des chiffres qui circulent, repris d'un article à l'autre, sans que personne ne sache d'où ils viennent.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Ce qui déclenche une restriction n'est presque jamais un nombre. C'est un comportement qui ne ressemble pas à celui d'une personne.",
        },
        { type: ArticleBlockType.H2, text: "Ce que LinkedIn regarde vraiment" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un compte n'est pas surveillé par un compteur unique, mais par un faisceau. Le volume en fait partie, il est loin d'être le plus discriminant.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La régularité.** Cent actions étalées sur une journée ressemblent à quelqu'un qui travaille. Cent actions en quinze minutes, à un script.",
            "**Le taux d'acceptation.** Beaucoup d'invitations sans réponse dit que tu vises mal, et c'est le signal le plus coûteux.",
            "**Les signalements.** Un message ressenti comme du spam se signale en deux clics. Quelques signalements pèsent bien plus que le volume.",
            "**La cohérence de la session.** Une activité continue jour et nuit, depuis une adresse qui ne correspond à rien, se repère sans effort.",
            "**Le contenu répété.** Le même texte envoyé à des centaines de personnes est reconnaissable, même reformulé automatiquement.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le deuxième point mérite qu'on s'y arrête, parce qu'il renverse le raisonnement habituel. Envoyer moins d'invitations mais mieux ciblées améliore ton taux d'acceptation, et un bon taux d'acceptation est précisément ce qui te protège. Le ciblage n'est pas seulement plus efficace, il est plus sûr.",
        },
        { type: ArticleBlockType.H2, text: "Le problème des outils qui agissent à ta place" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La plupart des solutions de prospection LinkedIn promettent du volume : des centaines d'invitations par semaine, des séquences qui partent seules, des visites de profil automatiques. Elles y arrivent, et c'est bien le problème.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Un outil qui agit seul",
            items: [
              "Il envoie même quand tu ne travailles pas",
              "Il vise large, parce que le volume est son argument",
              "Il répète un texte que tu n'as pas relu",
              "C'est ton compte qui porte le risque, pas le sien",
            ],
          },
          good: {
            title: "Un outil qui prépare et te laisse agir",
            items: [
              "Rien ne part sans que tu l'aies déclenché",
              "Il vise court, parce qu'il sait pourquoi chaque profil est là",
              "Il propose un texte que tu corriges avant d'envoyer",
              "Le risque reste au niveau de ce que tu fais réellement",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence n'est pas idéologique. Un outil qui agit seul optimise ce qu'il sait mesurer : le nombre d'actions. Personne n'a jamais gagné un client parce qu'une machine avait visité son profil.",
        },
        { type: ArticleBlockType.H2, text: "Combien contacter, en pratique" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Plutôt que de chercher un plafond, prends le problème par le seul bout qui compte : combien de messages peux-tu écrire en sachant à qui tu écris et pourquoi.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que tu envoies", "Ce que ça produit"],
          rows: [
            [
              "200 invitations génériques par semaine",
              "Un taux d'acceptation faible, quelques signalements, un compte fragilisé",
            ],
            [
              "20 messages écrits pour une personne précise",
              "Un taux de réponse qui rend la comparaison absurde",
            ],
            [
              "Une séquence automatique sur une liste achetée",
              "Des réponses négatives, et ton nom associé à du démarchage",
            ],
            [
              "Une prise de contact sur une situation repérée",
              "Une conversation qui commence, parfois un mois plus tard",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Vingt messages par semaine paraissent dérisoires quand on vient d'un outil qui en promet deux cents. Sur un trimestre, ça fait deux cent soixante conversations possibles, engagées avec des gens choisis. C'est plus que ce que la plupart des indépendants traitent réellement.",
        },
        { type: ArticleBlockType.H2, text: "Si ton compte est déjà restreint" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Arrête tout de suite l'outil qui agissait.** Tant qu'il tourne, le comportement continue.",
            "**Retire les invitations en attente.** Un stock d'invitations sans réponse entretient le mauvais signal.",
            "**Reviens à une activité normale quelques jours.** Lire, commenter, répondre, sans rien envoyer en masse.",
            "**Reprends avec un volume que tu peux justifier.** Chaque message doit avoir une raison propre à la personne.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "L'extension ne prospecte pas à ta place. Elle lit les profils que tu as sous les yeux, les confronte aux critères tirés de tes appels clients, et te dit lesquels correspondent et pourquoi. Ensuite c'est toi qui écris et qui envoies, à ton rythme, avec une raison propre à chaque personne.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cherche moins un plafond qu'une manière de travailler qui n'en approche jamais. Un bon ciblage fait monter ton taux d'acceptation, et c'est lui qui te protège. Le reste, les cadences, les scripts, les visites automatiques, optimise un nombre d'actions dont aucun client ne se souvient.",
        },
      ],
      faq: [
        {
          question: "Combien d'invitations LinkedIn peut-on envoyer par semaine ?",
          answer:
            "LinkedIn ne publie pas de seuil, et les chiffres qui circulent ne viennent d'aucune source officielle. Ce qui compte davantage est le taux d'acceptation : beaucoup d'invitations sans réponse est un signal plus coûteux qu'un volume élevé bien accepté.",
        },
        {
          question: "Les outils d'automatisation LinkedIn font-ils bannir ?",
          answer:
            "Pas mécaniquement, mais ils produisent exactement le comportement qui déclenche une restriction : régularité inhumaine, ciblage large, texte répété. Et c'est ton compte qui porte le risque, pas l'outil.",
        },
        {
          question: "Que faire si son compte LinkedIn est restreint ?",
          answer:
            "Arrêter immédiatement toute automatisation, retirer les invitations en attente, revenir quelques jours à une activité normale de lecture et de réponse, puis reprendre avec un volume que chaque message justifie individuellement.",
        },
        {
          question: "Vaut-il mieux envoyer peu de messages très ciblés ?",
          answer:
            "Oui, et pour deux raisons qui se renforcent. Le taux de réponse n'a rien à voir, et un bon taux d'acceptation est ce qui protège le compte. Le ciblage est à la fois la stratégie la plus efficace et la plus sûre.",
        },
      ],
    },
    en: {
      title: "LinkedIn prospecting: what actually gets an account restricted",
      excerpt:
        "The limits are not published, and the tools promising volume cross them on your behalf. What really triggers a restriction, and how to work without fearing it.",
      description:
        "Invitations, messages, automation: what genuinely exposes a LinkedIn account to a restriction, and how to prospect without depending on a tool that acts for you.",
      ctaTitle: "Prospect less, but better aimed",
      ctaText:
        "We qualify your network with criteria drawn from your calls, so you contact twenty people who answer rather than two hundred at random. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The question comes up in every conversation about LinkedIn prospecting, and it never gets a clear answer: at what point do you risk having your account restricted.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It gets no clear answer because LinkedIn publishes no thresholds. And the vacuum is filled with numbers passed from one article to the next, with nobody able to say where they came from.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "What triggers a restriction is almost never a number. It is behaviour that does not look like a person's.",
        },
        { type: ArticleBlockType.H2, text: "What LinkedIn actually watches" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An account is not judged by a single counter but by a cluster of signals. Volume is one of them, and far from the most telling.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Rhythm.** A hundred actions spread across a day look like someone working. A hundred in fifteen minutes look like a script.",
            "**Acceptance rate.** Many invitations going unanswered says you are aiming badly, and that is the most expensive signal of all.",
            "**Reports.** A message that feels like spam is reported in two clicks. A handful of reports weighs far more than volume.",
            "**Session coherence.** Continuous activity day and night, from an address matching nothing, is spotted without effort.",
            "**Repeated content.** The same text sent to hundreds of people is recognisable, even when reworded automatically.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The second point deserves a pause, because it inverts the usual reasoning. Sending fewer but better-aimed invitations raises your acceptance rate, and a good acceptance rate is exactly what protects you. Targeting is not only more effective, it is safer.",
        },
        { type: ArticleBlockType.H2, text: "The problem with tools that act for you" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most LinkedIn prospecting solutions promise volume: hundreds of invitations a week, sequences that send themselves, automatic profile visits. They deliver, and that is precisely the problem.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "A tool that acts on its own",
            items: [
              "It sends even when you are not working",
              "It aims wide, because volume is its selling point",
              "It repeats a text you never read back",
              "Your account carries the risk, not the tool",
            ],
          },
          good: {
            title: "A tool that prepares and lets you act",
            items: [
              "Nothing goes out unless you trigger it",
              "It aims narrow, because it knows why each profile is there",
              "It suggests a text you correct before sending",
              "The risk stays proportional to what you actually did",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference is not ideological. A tool that acts alone optimises what it can measure: the number of actions. Nobody ever won a customer because a machine visited their profile.",
        },
        { type: ArticleBlockType.H2, text: "How many to contact, in practice" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Rather than hunting for a ceiling, take the only end that matters: how many messages can you write while knowing who you are writing to, and why.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you send", "What it produces"],
          rows: [
            [
              "200 generic invitations a week",
              "A low acceptance rate, a few reports, a weakened account",
            ],
            [
              "20 messages written for one specific person",
              "A reply rate that makes the comparison absurd",
            ],
            [
              "An automated sequence on a bought list",
              "Negative replies, and your name attached to cold outreach",
            ],
            [
              "An approach built on a situation you spotted",
              "A conversation that starts, sometimes a month later",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Twenty messages a week sounds trivial coming from a tool that promises two hundred. Over a quarter it is two hundred and sixty possible conversations, started with people you chose. That is more than most independents actually handle.",
        },
        { type: ArticleBlockType.H2, text: "If your account is already restricted" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Stop the tool immediately.** As long as it runs, the behaviour continues.",
            "**Withdraw pending invitations.** A stock of unanswered invitations keeps the bad signal alive.",
            "**Go back to normal activity for a few days.** Read, comment, reply, send nothing in bulk.",
            "**Resume at a volume you can justify.** Every message must have a reason specific to the person.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "The extension does not prospect for you. It reads the profiles in front of you, checks them against criteria drawn from your customer calls, and tells you which ones fit and why. Then you write and you send, at your own pace, with a reason specific to each person.",
          linkLabel: "See how prospect search works",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Look less for a ceiling than for a way of working that never approaches one. Good targeting raises your acceptance rate, and that is what protects you. The rest, the cadences, the scripts, the automatic visits, optimises a number of actions no customer remembers.",
        },
      ],
      faq: [
        {
          question: "How many LinkedIn invitations can you send per week?",
          answer:
            "LinkedIn publishes no threshold, and the numbers in circulation come from no official source. What matters more is the acceptance rate: many invitations going unanswered is a costlier signal than a high volume that is well accepted.",
        },
        {
          question: "Do LinkedIn automation tools get you banned?",
          answer:
            "Not mechanically, but they produce exactly the behaviour that triggers a restriction: inhuman regularity, wide targeting, repeated text. And your account carries the risk, not the tool.",
        },
        {
          question: "What should you do if your LinkedIn account is restricted?",
          answer:
            "Stop all automation immediately, withdraw pending invitations, return for a few days to normal reading and replying, then resume at a volume where each message justifies itself individually.",
        },
        {
          question: "Is it better to send few, highly targeted messages?",
          answer:
            "Yes, for two reasons that reinforce each other. The reply rate is not comparable, and a good acceptance rate is what protects the account. Targeting is both the most effective strategy and the safest.",
        },
      ],
    },
  },
};
